<template>
  <b-autocomplete
    field="name"
    v-model="portfolioName"
    v-bind:data="portfolios"
    v-bind:placeholder="placeholder"
    icon="search"
    :size="size"
    icon-pack="fas"
    clearable
    open-on-focus
    v-bind:loading="loading"
    @icon-right-click="clearInput()"
    @typing="deboucedResults"
    @select="option => (selectPortfolio(option))"
    custom-class="importantWhite blackBorder"
    :disabled="disabled"
  >
    <template slot-scope="props">
      <div class="media tw-flex tw-items-center tw-gap-1">
        <div class="media-left">
          <PortfolioIcon
            v-bind:portfolioType="searchTickers ? 'ticker' : props.option.portfolio_type"
          />
        </div>

        <!-- In this component the default text size is 0.875rem (same as tw-text-sm) because of the global style applied to .dropdown-item in dash3.css -->
        <!-- So text size is the same regardless of length -->
        <!-- Should be possible to fix but no time at the moment -->
        <div :class="['!tw-whitespace-normal', props.option.name.length > 100 ? 'tw-text-sm' : '']" v-if="resultType == 'portfolios'">
          <p :title="props.option.name">
            {{ props.option.name.length > 150 ? props.option.name.substring(0, 150).trim() + '...' : props.option.name }}
          </p>
          <p class="tw-text-sm tw-text-gray-500" v-if="!props.option?.ticker_id">
            {{
              new Date(props.option.created_date || props.option.created_at).toLocaleString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
              })
            }}
          </p>
        </div>

        <div class="media-content" v-else-if="resultType == 'tickers' && props.option.ticker">
          <strong>{{ props.option.ticker.symbol }}</strong><span v-show="!props.option.ticker.has_metrics">***</span>
          <br>{{ props.option.ticker.full_name }}
        </div>
      </div>
    </template>

    <template slot="empty">
      <div v-if="!loading">No results found</div>
      <div v-if="loading">Searching...</div>
    </template>
  </b-autocomplete>

</template>

<script>
import debounce from 'lodash/debounce';
import eventBus from '@frontend/shared/EventBus.js';

import PortfolioIcon from './PortfolioIcon.vue';
import Symbol from '@frontend/dash3/js/shared/Symbol.js';
import { apiFetch } from '../../../../api/api';

export default {
  data() {
    return {
      portfolioName: '',
      loading: false,
      portfolios: [],
      resultType: 'portfolios', // or 'tickers'
    };
  },
  components: {
    PortfolioIcon,
  },
  props: {
    placeholder: String,
    selected: Object,
    selectedId: String,
    portfolioType: {
      type: String,
      default: null,
    },
    callback: Function,
    size: String,
    searchTickers: {
      type: Boolean,
      default: false,
    },
    notAllowedIds: {
      type: Array,
      default: () => [],
    },
    hideEmptyPortfolios: Boolean,
    shareownersAndPortfolios: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
    not_include_shareowners: {
      type: Boolean,
      default: false,
    },
    lightSerializer: {
      type: Boolean,
      default: false,
    },
    returnEntities: {
      type: Boolean,
      default: false,
    },
  },
  computed: {},
  methods: {
    /* eslint-disable camelcase */
    selectPortfolio: async function(option) {
      let current_option;
      if (this.searchTickers && option) {
        current_option = await this.getTickerPortfolio(option.ticker.symbol);
      } else {
        current_option = option;
      }
      if (this.callback) {
        this.callback.call(this, JSON.parse(JSON.stringify(current_option)));
      }
      eventBus.$emit('TypeaheadPortfolioSearch:portfolio-selected', current_option);
    },
    /* eslint-enable camelcase */
    getTickerPortfolio: async function(tickerSymbol) {
      const symbol = new Symbol(tickerSymbol).encode();
      const encodedTicker = encodeURIComponent(symbol);
      const url = `/api/v1/portfolios/$${encodedTicker}/`;
      const response = await apiFetch(url);
      const data = await response.json();
      if (data) {
        return data.portfolio;
      }
      this.loading = false;
      return { id: null, portfolio_type: null };
    },
    getResults: async function(searchText) {
      this.loading = true;
      this.portfolios = [];
      if (this.searchTickers) {
        this.resultType = 'tickers';

        const encodedSearchText = encodeURIComponent(`$${searchText}`);
        const params = {
          'search': encodedSearchText,
        };

        const url = `/api/v1/tickers/?${new URLSearchParams(params)}`;
        const response = await apiFetch(url);
        const data = await response.json();

        if (data.tickers) {
          this.portfolios.push(...data.tickers);
        }
      } else {
        if (!this.shareownersAndPortfolios) {
          this.resultType = 'portfolios';
        } else {
          this.resultType = '';
        }
        const params = { 'search': searchText };
        if (this.not_include_shareowners) {
          console.log('NOT INCLUDE SHAREOWNERS TRUE');
          params.not_include_shareowners = true;
        }
        if (this.portfolioType) {
          params.type = this.portfolioType;
        }
        if (this.lightSerializer) {
          params.light_serializer = true;
        }
        if (this.returnEntities) {
          params.return_entities = true;
          params.has_one_metric_coverage = true;
        }
        const url = `/api/v1/portfolios/?${new URLSearchParams(params)}`;
        const response = await apiFetch(url);
        const data = await response.json();

        if (data.portfolios) {
          this.portfolios.push(...data.portfolios.filter(portfolio => {
            // Exclude specific portfolio ids
            if (this.notAllowedIds.includes(portfolio.id)) return false;

            // Hide portfolios with no holdings if flag is true
            return portfolio.has_holdings || !this.hideEmptyPortfolios;
          }));
        }
      }
      this.loading = false;
    },
    _applyName: function() {
      if (this.selected?.id) {
        this.portfolioName = this.selected.name || (this.selected.first_name && this.selected.last_name && `${this.selected.first_name} ${this.selected.last_name}`) || this.selected.nickname;
        return;
      }
      this.portfolioName = '';
    },
    clearInput: function() {
      /* eslint-disable-next-line vue/no-mutating-props */
      this.selected = null;
      this._applyName();
    },
    deboucedResults: debounce(async function(searchText) {
      // TODO: This should probably be investigated but I don't want to risk breaking anything right now
      /* eslint-disable-next-line */
      this.getResults(searchText, this.not_include_shareowners);
    }, 400),
  },
  watch: {
    selected: function() {
      this._applyName();
    },
    searchTickers: function() {
      this.portfolios = [];
      if (this.searchTickers) {
        this.resultType = 'tickers';
      } else {
        this.resultType = 'portfolios';
        this.getResults('');
      }
    },
  },
  mounted: function() {
    this._applyName();
    this.getResults('');
  },
  created: async function() {
    if (this.selectedId) {
      const url = `/api/v1/portfolios/${this.selectedId}/`;
      const response = await apiFetch(url);
      const data = await response.json();
      if (data.portfolio) {
        /* eslint-disable-next-line vue/no-mutating-props */
        this.selected = data.portfolio;
        this.selectPortfolio(data.portfolio);
      }
    }
  },
  beforeUpdate: function() {
    this._applyName();
  },
};
</script>
